import { useLocation } from 'wouter';
import { useCallback, useMemo, useState, useEffect, useRef } from 'react';

/**
 * Custom hook to sync state with URL query parameters
 *
 * @param key - The query parameter key
 * @param defaultValue - The default value when parameter is not present
 * @param options - Optional configuration
 * @param options.debounceMs - Debounce delay in ms for URL updates (default: 0 for immediate)
 * @returns [value, setValue] tuple similar to useState
 *
 * @example
 * const [entityType, setEntityType] = useUrlState('entityType', '');
 * const [searchTerm, setSearchTerm] = useUrlState('search', '', { debounceMs: 300 });
 * const [sortField, setSortField] = useUrlState('sort', 'createdAt');
 *
 * // URL: /accounts?entityType=company&sort=createdAt
 * // Refreshing page preserves filter state
 */
export function useUrlState<T extends string | number | boolean = string>(
  key: string,
  defaultValue: NoInfer<T>,
  options?: { debounceMs?: number }
): [T, (value: T) => void] {
  const [location, setLocation] = useLocation();
  const debounceMs = options?.debounceMs ?? 0;
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track the search string separately to trigger re-renders when it changes
  const [searchString, setSearchString] = useState(() =>
    typeof window !== 'undefined' ? window.location.search : ''
  );

  // Listen for URL changes (including programmatic ones)
  useEffect(() => {
    const handleLocationChange = () => {
      const newSearch = window.location.search;
      setSearchString(newSearch);
    };

    // Check on every location change from wouter
    handleLocationChange();

    // Also listen for popstate events (browser back/forward)
    window.addEventListener('popstate', handleLocationChange);

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
    };
  }, [location]);

  // Get the initial value from URL
  // Note: wouter's useLocation() returns only pathname, not query string
  // We need to use window.location.search to get query parameters
  const urlValue = useMemo(() => {
    const search = searchString.slice(1); // Remove leading '?'
    const params = new URLSearchParams(search);
    const rawValue = params.get(key);

    if (rawValue === null) return defaultValue;

    // Parse based on type
    if (typeof defaultValue === 'number') return Number(rawValue) as T;
    if (typeof defaultValue === 'boolean') return (rawValue === 'true') as T;
    return rawValue as T;
  }, [searchString, key, defaultValue]);

  // Local state for immediate UI updates (only used when debouncing)
  const [localValue, setLocalValue] = useState<T>(urlValue);

  // Sync local value when URL changes externally (e.g., browser back/forward)
  useEffect(() => {
    setLocalValue(urlValue);
  }, [urlValue]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const updateUrl = useCallback(
    (newValue: T) => {
      const search = typeof window !== 'undefined' ? window.location.search.slice(1) : '';
      const params = new URLSearchParams(search);

      // Clean URL: remove param if value equals default
      if (newValue === defaultValue || newValue === '' || newValue === null || newValue === undefined) {
        params.delete(key);
      } else {
        params.set(key, String(newValue));
      }

      // Update URL without reload
      const pathname = location;
      const hash = typeof window !== 'undefined' ? window.location.hash : '';
      const searchStr = params.toString();
      const newUrl = `${pathname}${searchStr ? `?${searchStr}` : ''}${hash}`;

      setLocation(newUrl, { replace: true });

      // Force searchString update immediately after setLocation
      // This ensures the component re-renders with the new value
      setSearchString(searchStr ? `?${searchStr}` : '');
    },
    [setLocation, location, key, defaultValue]
  );

  const setValue = useCallback(
    (newValue: T) => {
      if (debounceMs > 0) {
        // Immediately update local state for responsive UI
        setLocalValue(newValue);

        // Clear existing timer
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        // Debounce the URL update
        debounceTimerRef.current = setTimeout(() => {
          updateUrl(newValue);
        }, debounceMs);
      } else {
        // No debouncing - update URL immediately
        updateUrl(newValue);
      }
    },
    [debounceMs, updateUrl]
  );

  // Return local value when debouncing, URL value otherwise
  return [debounceMs > 0 ? localValue : urlValue, setValue];
}
